import React, { useEffect, useState } from 'react'
import { Formik, Form, FieldArray, Field } from 'formik'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import SharedButton from '../Shared/SharedButton'
import TextInput from '../Shared/TextInput'
import AutoCompleteField from '../Shared/AutoCompleteField'
import DropdownComponent from '../Shared/DropdownComponent'
import PageHeader from '../Shared/PageHeader'
import i18next from 'i18next'
import { Button } from 'primereact/button'
import toast from 'react-hot-toast'
import { useLocation, useNavigate } from 'react-router-dom'
import { fetchData } from '../../helpers/fetchData'
import SwitchInput from '../Shared/SwitchInput'
import { RiDeleteBin5Line } from 'react-icons/ri'
import { FaCheckDouble } from 'react-icons/fa6'


const amountTypeOptions = [
  { label: 'Patient Share', value: 'patient_share' },
  { label: 'Company Share', value: 'company_share' },
  { label: 'Patient Vat', value: 'patient_vat' },
  { label: 'Company Vat', value: 'company_vat' },
  { label: 'Gross Amount', value: 'gross_amount' },
  { label: 'Discount Amount', value: 'discount_amount' },
  { label: 'Total Vat', value: 'total_vat' },
  { label: 'Total Amount With Vat', value: 'total_amount_with_vat' },
  { label: 'Total Amount Without Vat', value: 'total_amount_without_vat' }
]

const accountTypeOptions = [
    { label: 'General', value: 'general' },
    { label: 'Patient', value: 'patient' },
    { label: 'Company', value: 'company' }
]

const NewCreateSeetingDialog = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const lang = i18next.language
  const editData = location?.state?.editData
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation()

  const transactionTypeOptions = [
    { label: t('Debit'), value: 'debit' },
    { label: t('Credit'), value: 'credit' }
  ]


  const titleOptions = React.useMemo(() => [
    { label: t('Cash Invoices'), value: 'cash_invoices' },
    { label: t('Cash Invoices Paid'), value: 'cash_invoices_paid' },
    { label: t('Cash Invoices UnPaid'), value: 'cash_invoices_unpaid' },
    { label: t('Insurance Invoices'), value: 'insurance_invoices' },
    { label: t('Insurance Invoices Paid'), value: 'insurance_invoices_paid' },
    { label: t('Insurance Invoices UnPaid'), value: 'insurance_invoices_unpaid' },
    { label: t('Credit Invoices'), value: 'credit_invoices' },
    { label: t('Purchase Entries'), value: 'purchase_entries' },
    { label: t('Payrolls'), value: 'payrolls' },
    { label: t('Payrolls'), value: 'payrolls' }
  ], [t])



  const modalTitle = () => {
    let title = editData ? 'update_settings' : 'create_settings'
    return t(title)
  }

  const initialValues = {
    title: editData?.title ?? '',
    is_active: editData?.is_active ?? false,
    transactions: editData?.transactions || []
  }

  const validationSchema = yup.object().shape({
    title: yup.string().required(t('required')),
    transactions: yup.array().of(
      yup.object().shape({
        account_code: yup.string().required(t('required')),
        transaction_type: yup.string(),
        amount_type: yup.string()
      })
    )
  })

  const handleCreateSettings = async payload => {
    try {
      setLoading(true)
      const res = await fetchData('POST', '/tsetting/create', payload)
      toast.success(res?.message)
      if (res?.message == 'Transaction setting created successfully') {
        navigate(-1)
      }
    } catch (error) {
      toast.error(error.message || t('failed_to_create_settings'))
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateSettings = async payload => {
    try {
      setLoading(true)
      const res = await fetchData('PUT', '/tsetting/update', payload)
      toast.success(res?.message)
      if (res?.message == 'Transaction setting updated successfully') {
        navigate(-1)
      }
    } catch (error) {
      toast.error(error.message || t('failed_to_update_settings'))
    } finally {
      setLoading(false)
    }
  }

  const handleFormSubmit = values => {
    const transactions = values?.transactions || []

    const hasCredit = transactions.some(t => t?.transaction_type === 'credit')
    const hasDebit = transactions.some(t => t?.transaction_type === 'debit')

    if (!hasCredit || !hasDebit) {
      const missingType = !hasDebit ? 'debit' : 'credit'
      toast.error(`There should be at least one ${missingType} account`)
      return
    }

    if (values?.transactions.length < 1) {
      toast.error('Please Select Account')
      return
    }

    let payload = {
      ...(editData && { ...editData }),
      title: values?.title,
      ...(editData && { is_active: values?.is_active }),
      transactions: values?.transactions.map(transaction => ({
        ...transaction,
        account_code: String(transaction?.account_code)
      }))
    }

    if (editData) {
      handleUpdateSettings(payload)
    } else {
      handleCreateSettings(payload)
    }
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize={true}
      >
        {props => (
          <Form onSubmit={props.handleSubmit}>
            <div className='flex flex-col w-full'>
              <PageHeader title={modalTitle()} backPath={-1} />
              
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 w-full'>
                <div>
                  <DropdownComponent
                    filter
                    label={t('title')}
                    options={titleOptions}
                    name={'title'}
                    onChange={e => {
                      props?.setFieldValue('title', e.value)
                    }}
                    value={props?.values?.title}
                    placeholder={t('Enter title')}
                    className=' !w-full'
                  />
                  {props.errors.title && props.touched.title && (
                    <div className='text-red-500 text-sm mt-1'>
                      {props.errors.title}
                    </div>
                  )}
                </div>
                <AutoCompleteField
                  label={t('account')}
                  name={'account'}
                  fieldUrl='searchAccounts'
                  placeholder={`${t('select')} ${t('account')}`}
                  className='!w-full  !hover:border-primary !outline-none !shadow-none'
                  inputClassName={'!h-[42px]'}
                  disabled={!props?.values?.title}
                  optionsFormat={data =>
                    Object.entries(data || {}).map(([id, account]) => ({
                      label:
                        lang === 'ar'
                          ? `${account?.name_ar} ${account?.code}`
                          : `${account?.name_en} ${account?.code}`,
                      value: Number(account?.id),
                      code: account?.code,
                      name_ar: account?.name_ar,
                      name_en: account?.name_en
                    }))
                  }
                  onselect={e => {
                     const selected = e.value || e;
                    if (!selected) return;

                    const name = selected.name_en || '';
                    const [first] = name.split('-');
                    
                    let type = 'general';
                    if (first && typeof first === 'string') {
                        const trimmedFirst = first.trim().toLowerCase();
                        if (trimmedFirst === 'patient') type = 'patient';
                        else if (trimmedFirst === 'company') type = 'company';
                    }

                    const newObj = {
                      account_type: type,
                      account_code: selected.code,
                      account_name_en: selected.name_en,
                      account_name_ar: selected.name_ar,
                      transaction_type: 'debit',
                      amount_type: props.values.title
                    }
                    const updatedTransactions = [
                      ...(props.values.transactions || []),
                      newObj
                    ]
                    props.setFieldValue('transactions', updatedTransactions)
                    setTimeout(() => props.setFieldValue('account', ''), 100)
                  }}
                  onSelect={e => {
                     const selected = e.value || e;
                    if (!selected) return;

                    const name = selected.name_en || '';
                    const [first] = name.split('-');
                    
                    let type = 'general';
                    if (first && typeof first === 'string') {
                        const trimmedFirst = first.trim().toLowerCase();
                        if (trimmedFirst === 'patient') type = 'patient';
                        else if (trimmedFirst === 'company') type = 'company';
                    }

                    const newObj = {
                      account_type: type,
                      account_code: selected.code,
                      account_name_en: selected.name_en,
                      account_name_ar: selected.name_ar,
                      transaction_type: 'debit',
                      amount_type: props.values.title
                    }
                    const updatedTransactions = [
                      ...(props.values.transactions || []),
                      newObj
                    ]
                    props.setFieldValue('transactions', updatedTransactions)
                    setTimeout(() => props.setFieldValue('account', ''), 100)
                  }}
                />
                {editData && (
                  <div className='flex items-center'>
                  <SwitchInput
                    label={t('Active')}
                    value={props.values.is_active}
                    handleChange={e => {
                      props.setFieldValue('is_active', e.value)
                    }}
                  />
                  </div>
                )}
              </div>
              {props?.values?.transactions?.length > 0 && (
                <FieldArray name='transactions'>
                  {({ push, remove, form }) => (
                    <div className='overflow-auto'>
                      <table className='w-full mt-2 border !rounded-md border-gray-200 whitespace-nowrap'>
                        <thead className=''>
                          <tr className='!bg-gray-300'>
                            <th className='table-header bg-gray-300 !font-bold px-4 py-2 text-left'>
                              {t('Account Type')}
                            </th>
                            <th className='table-header bg-gray-300 !font-bold px-4 py-2 text-left'>
                              {t('Account Code')}
                            </th>
                            {lang === 'ar' ? (
                              <th className='table-header bg-gray-300 !font-bold px-4 py-2 text-left'>
                                {t('Account Name (AR)')}
                              </th>
                            ) : (
                              <th className='table-header bg-gray-300 !font-bold px-4 py-2 text-left'>
                                {t('Account Name (EN)')}
                              </th>
                            )}
                            <th className='table-header bg-gray-300 !font-bold px-4 py-2 text-left'>
                              {t('Transaction Type')}
                            </th>
                            <th className='table-header bg-gray-300 !font-bold px-4 py-2 text-left'>
                              {t('Amount Type')}
                            </th>

                            {editData && (
                              <th className='table-header bg-gray-300 !font-bold px-4 py-2 text-left'>
                                {t('Active')}
                              </th>
                            )}
                            <th className='table-header bg-gray-300 !font-bold px-4 py-2 text-left'>
                              {t('Actions')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className='rounded'>
                          {form.values.transactions.map(
                            (transaction, index) => (
                              <tr key={index} className='border-t'>
                                <td className='!px-[0.35rem] !py-[0.6rem]'>
                                  <DropdownComponent
                                    disabled
                                    options={accountTypeOptions}
                                    name={`transactions[${index}].account_type`}
                                    value={
                                      props?.values?.transactions[index]
                                        ?.account_type
                                    }
                                    onChange={e => {
                                      props?.setFieldValue(
                                        `transactions[${index}].account_type`,
                                        e.value
                                      )
                                    }}
                                    placeholder='Select Account Type'
                                    className='!w-full'
                                  />
                                  {props.errors.transactions?.[index]
                                    ?.account_type &&
                                    props.touched.transactions?.[index]
                                      ?.account_type && (
                                      <div className='text-red-500 text-sm'>
                                        {
                                          props.errors.transactions[index]
                                            .account_type
                                        }
                                      </div>
                                    )}
                                </td>
                                <td className='!px-[0.35rem] !py-[0.6rem]'>
                                  <Field
                                    name={`transactions[${index}].account_code`}
                                    as={TextInput}
                                    placeholder='Enter account code'
                                    disabled
                                    className='!p-1.5'
                                  />
                                  {props.errors.transactions?.[index]
                                    ?.account_code &&
                                    props.touched.transactions?.[index]
                                      ?.account_code && (
                                      <div className='text-red-500 text-sm'>
                                        {
                                          props.errors.transactions[index]
                                            .account_code
                                        }
                                      </div>
                                    )}
                                </td>
                                {lang == 'ar' ? (
                                  <td className='!px-[0.35rem] !py-[0.6rem]'>
                                    <Field
                                      name={`transactions[${index}].account_name_ar`}
                                      as={TextInput}
                                      placeholder='Enter account name'
                                      disabled
                                      className='!p-1.5'
                                    />
                                    {props.errors.transactions?.[index]
                                      ?.account_name_ar &&
                                      props.touched.transactions?.[index]
                                        ?.account_name_ar && (
                                        <div className='text-red-500 text-sm'>
                                          {
                                            props.errors.transactions[index]
                                              .account_name_ar
                                          }
                                        </div>
                                      )}
                                  </td>
                                ) : (
                                  <td className='!px-[0.35rem] !py-[0.6rem]'>
                                    <Field
                                      name={`transactions[${index}].account_name_en`}
                                      as={TextInput}
                                      placeholder='Enter account name'
                                      disabled
                                      className='!p-1.5'
                                    />
                                    {props.errors.transactions?.[index]
                                      ?.account_name_en &&
                                      props.touched.transactions?.[index]
                                        ?.account_name_en && (
                                        <div className='text-red-500 text-sm'>
                                          {
                                            props.errors.transactions[index]
                                              .account_name_en
                                          }
                                        </div>
                                      )}
                                  </td>
                                )}
                                <td className='!px-[0.35rem] !py-[0.6rem]'>
                                  <DropdownComponent
                                    options={transactionTypeOptions}
                                    name={`transactions[${index}].transaction_type`}
                                    value={
                                      props?.values?.transactions[index]
                                        ?.transaction_type
                                    }
                                    onChange={e => {
                                      props?.setFieldValue(
                                        `transactions[${index}].transaction_type`,
                                        e.value
                                      )
                                    }}
                                    placeholder='Select Type'
                                    className='!w-full'
                                  />
                                </td>
                                <td className='!px-[0.35rem] !py-[0.6rem]'>
                                  <DropdownComponent
                                    options={titleOptions}
                                    name={`transactions[${index}].amount_type`}
                                    value={
                                      props?.values?.transactions[index]
                                        ?.amount_type
                                    }
                                    onChange={e => {
                                      props?.setFieldValue(
                                        `transactions[${index}].amount_type`,
                                        e.value
                                      )
                                    }}
                                    placeholder='Select Type'
                                    className='!w-full'
                                  />
                                </td>

                                {editData && (
                                  <td className='!px-[0.35rem] !py-[0.6rem]'>
                                    <div className='flex justify-center'>
                                    <SwitchInput
                                      name={`transactions[${index}].is_active`}
                                      value={
                                        props?.values?.transactions[index]
                                          ?.is_active
                                      }
                                      handleChange={e => {
                                        const transactions =
                                          props?.values?.transactions || []
                                        const current = transactions[index]

                                        const activeTransactions =
                                          transactions.filter(
                                            t => t?.is_active !== false
                                          )

                                        const activeCredits =
                                          activeTransactions.filter(
                                            t => t.transaction_type === 'credit'
                                          )
                                        const activeDebits =
                                          activeTransactions.filter(
                                            t => t.transaction_type === 'debit'
                                          )

                                        if (
                                          current?.is_active &&
                                          ((current.transaction_type ===
                                            'credit' &&
                                            activeCredits.length === 1) ||
                                            (current.transaction_type ===
                                              'debit' &&
                                              activeDebits.length === 1))
                                        ) {
                                          toast.error(
                                            `There should be at least one ${current.transaction_type} account active`
                                          )
                                          return
                                        }

                                        props.setFieldValue(
                                          `transactions[${index}].is_active`,
                                          e.value
                                        )
                                      }}
                                    />
                                    </div>
                                  </td>
                                )}
                                <td className='!px-[0.35rem] !py-[0.6rem]'>
                                  <div className='flex justify-center items-center h-full'>
                                    <RiDeleteBin5Line
                                      size={20}
                                      className='cursor-pointer text-red-500'
                                      onClick={() => remove(index)}
                                    />
                                  </div>
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </FieldArray>
              )}
              <div className='flex justify-end gap-2 mt-2'>
                <SharedButton
                    icon='pi pi-times'
                    label={t('Cancel')}
                    buttonType='reset'
                    onClick={() => {
                        navigate(-1);
                    }}
                />
                <SharedButton
                    icon={<FaCheckDouble size={12} className='me-2' />}
                    label={t('Submit')}
                    className='submit-btn'
                    type='submit'
                    disabled={loading}
                />
              </div>
            </div>

          </Form >
        )}
      </Formik >
    </div >
  )
}

export default NewCreateSeetingDialog
